import type { User } from '@supabase/supabase-js';
import { assertLocalDataOwner, getSettings, listInvoices, listPayments, listQuotes, saveSyncSnapshot } from './db';
import { supabase } from './supabase';
import { withDatabaseRetry } from './supabaseRetry';
import { withAbortTimeout } from './async';
import { errorMessage } from './errors';
import { clearCloudFileCache, cloudContentEquals, cloudFileTransfer, hasCloudFileReferences, localCloudFileSources, referencesCloudKeys } from './cloudFiles';
import { cloudReferenceKeys, missingCloudFileKeys, resolveMissingCloudFiles } from './cloudFileState';
import { readR2Catalog, writeR2Row } from './r2Sync';
import {syncQueueEnabled} from './syncQueue';
import type { AppSettings, Invoice, Payment, Quote, SyncState } from '../types';

type SyncRecord = {
  id: string;
  version: number;
  updatedAt: string;
  deletedAt: string | null;
  syncState: SyncState;
  cloudRevision?: number;
  trashExpiresAt?:string|null;purgedAt?:string|null;trashLegacy?:boolean;
};

type CloudDocument<T> = {
  id: string;
  user_id: string;
  document_type: 'quote' | 'invoice';
  payload: T;
  version: number;
  updated_at: string;
  deleted_at: string | null;
  cloud_revision?: number;
  trash_expires_at?:string|null;purged_at?:string|null;trash_legacy?:boolean;
};

type CloudWrite = {
  id: string;
  user_id: string;
  document_type: 'quote' | 'invoice';
  payload: unknown;
  version: number;
  updated_at: string;
  deleted_at: string | null;
  cloud_revision?: number;
  trash_expires_at?:string|null;purged_at?:string|null;trash_legacy?:boolean;
};

type InvoiceBundle = Invoice & { cloudPayments?: Payment[] };
type CloudSettings = Pick<AppSettings, 'company' | 'quoteSequenceYear' | 'lastQuoteSequence' | 'invoiceSequenceYear' | 'lastInvoiceSequence' | 'receiptSequenceYear' | 'lastReceiptSequence' | 'cloudSettingsUpdatedAt'>;
type CloudSettingsPayload = { recordType: 'settings'; settings: CloudSettings };

export type SyncStage = 'local' | 'download' | 'upload' | 'save' | 'enable' | 'queued' | 'running' | 'upload-queued';
export const syncStageLabel: Record<SyncStage, string> = {
  local: 'กำลังเตรียมข้อมูล',
  download: 'กำลังอ่าน Cloud',
  upload: 'กำลังอัปโหลด',
  save: 'กำลังบันทึกในเครื่อง',
  enable: 'กำลังเปิด Cloud Sync',
  queued: 'รอคิวซิงค์ · ใช้งานในเครื่องต่อได้',
  'upload-queued': 'รอคิวอัปโหลด · ใช้งานในเครื่องต่อได้',
  running: 'กำลังซิงค์และตรวจไฟล์บน Cloud',
};

export class CloudSyncError extends Error {
  constructor(readonly stage: SyncStage, readonly cause: unknown) {
    super(errorMessage(cause, 'Cloud Sync ไม่สำเร็จ'));
    this.name = 'CloudSyncError';
  }
}

const cloudSettingsFrom = (settings: AppSettings): CloudSettings => ({
  company: settings.company,
  quoteSequenceYear: settings.quoteSequenceYear,
  lastQuoteSequence: settings.lastQuoteSequence,
  invoiceSequenceYear: settings.invoiceSequenceYear,
  lastInvoiceSequence: settings.lastInvoiceSequence,
  receiptSequenceYear: settings.receiptSequenceYear,
  lastReceiptSequence: settings.lastReceiptSequence,
  cloudSettingsUpdatedAt: settings.cloudSettingsUpdatedAt,
});

const mergeCollection = <T extends SyncRecord>(
  user: User,
  documentType: 'quote' | 'invoice',
  local: T[],
  allCloudRows: CloudDocument<unknown>[],
  acknowledged = new Set<string>(),
) => {
  const cloudRows = allCloudRows.filter((row) => row.document_type === documentType && (documentType !== 'quote' || row.id !== user.id)) as CloudDocument<T>[];
  const cloudById = new Map(cloudRows.map((row) => [row.id, row]));
  for (const record of local) {
    const remote = cloudById.get(record.id);
    if (record.syncState === 'pending' && remote && !remote.purged_at && record.cloudRevision !== undefined
      && record.cloudRevision !== remote.cloud_revision && !acknowledged.has(record.id)) throw new Error('cloud_document_conflict');
  }
  const pending = local.filter((record) => {
    const remote = cloudById.get(record.id);
    if(record.purgedAt||remote?.purged_at)return false;
    if (!remote) return true;
    if (record.syncState === 'synced' && record.version <= remote.version && record.updatedAt <= remote.updated_at) return false;
    return record.version > remote.version || (record.version === remote.version && record.updatedAt > remote.updated_at);
  });
  const rowsToUpsert: CloudWrite[] = pending.map((record) => ({
    id: record.id,
    user_id: user.id,
    document_type: documentType,
    payload: { ...record, syncState: 'synced' },
    version: record.version,
    updated_at: record.updatedAt,
    deleted_at: record.deletedAt,
  }));
  const remaining = new Map(local.map((record) => [record.id, record]));
  const merged: T[] = [];
  for (const row of cloudRows) {
    const current = remaining.get(row.id);
    const remote = {
      ...row.payload,
      id: row.id,
      version: row.version,
      updatedAt: row.updated_at,
      deletedAt: row.deleted_at,
      syncState: 'synced',
      cloudRevision: row.cloud_revision,
      trashExpiresAt:row.trash_expires_at,purgedAt:row.purged_at,trashLegacy:row.trash_legacy,
    } as T;
    if (row.purged_at || !current || row.version > current.version || (row.version === current.version && row.updated_at > current.updatedAt)) merged.push(remote);
    else merged.push({ ...current, syncState: 'synced', cloudRevision: row.cloud_revision,trashExpiresAt:row.trash_expires_at,purgedAt:row.purged_at,trashLegacy:row.trash_legacy });
    remaining.delete(row.id);
  }
  for (const record of remaining.values()) merged.push({ ...record, syncState: 'synced' });
  return { merged, rowsToUpsert };
};

const mergeAccountSettings = (user: User, local: AppSettings, allCloudRows: CloudDocument<unknown>[]): { merged: AppSettings; rowToUpsert: CloudWrite | null } => {
  const data = allCloudRows.find((row) => row.id === user.id && row.document_type === 'quote');
  const remotePayload = data?.payload as CloudSettingsPayload | undefined;
  const remoteSettings = remotePayload?.recordType === 'settings' ? remotePayload.settings : null;
  const localUpdatedAt = local.cloudSettingsUpdatedAt ?? '';
  const remoteUpdatedAt = remoteSettings?.cloudSettingsUpdatedAt ?? data?.updated_at ?? '';
  if (remoteSettings && localUpdatedAt > remoteUpdatedAt && local.cloudCatalogRevision !== undefined
    && local.cloudCatalogRevision !== data?.cloud_revision) throw new Error('cloud_document_conflict');
  if (remoteSettings && remoteUpdatedAt === localUpdatedAt) return { merged: { ...local, cloudCatalogRevision: data?.cloud_revision }, rowToUpsert: null };
  if (remoteSettings && remoteUpdatedAt > localUpdatedAt) {
    return {
      merged: { ...local, ...remoteSettings, cloudCatalogRevision: data?.cloud_revision, company: { ...local.company, ...remoteSettings.company } },
      rowToUpsert: null,
    };
  }

  const updatedAt = localUpdatedAt || new Date().toISOString();
  const settings = { ...local, cloudSettingsUpdatedAt: updatedAt };
  const rowToUpsert: CloudWrite = {
    id: user.id,
    user_id: user.id,
    document_type: 'quote',
    payload: { recordType: 'settings', settings: cloudSettingsFrom(settings) },
    version: Math.max(1, Number(data?.version ?? 0) + 1),
    updated_at: updatedAt,
    deleted_at: null,
  };
  return { merged: settings, rowToUpsert };
};

const preserveNewerLocal = <T extends SyncRecord>(merged: T[], latest: T[]) => {
  const byId = new Map(merged.map((record) => [record.id, record]));
  for (const record of latest) {
    const previous = byId.get(record.id);
    if(previous?.purgedAt)continue;
    if (!previous || record.version > previous.version || (record.version === previous.version && record.updatedAt > previous.updatedAt)) {
      byId.set(record.id, { ...record, cloudRevision: previous?.cloudRevision ?? record.cloudRevision });
    }
  }
  return [...byId.values()];
};

async function performSync(user: User, signal: AbortSignal, onStage: (stage: SyncStage) => void) {
  if (!supabase) throw new Error('ยังไม่ได้ตั้งค่า Supabase');
  const syncClient = supabase;
  let token: string | undefined;
  const sessionToken = async () => {
    if (!token) {
      const { data: sessionData, error: sessionError } = await syncClient.auth.getSession();
      if (sessionError) throw sessionError;
      if (!sessionData.session || sessionData.session.user.id !== user.id) throw new Error('Session expired');
      token = sessionData.session.access_token;
    }
    return token;
  };
  onStage('local');
  assertLocalDataOwner(user.id);
  const [localQuotes, localInvoices, localPayments, localSettings] = await Promise.all([listQuotes(true), listInvoices(true), listPayments(), getSettings()]);
  signal.throwIfAborted();
  const invoiceBundles: InvoiceBundle[] = localInvoices.map((invoice) => ({
    ...invoice,
    cloudPayments: localPayments.filter((payment) => payment.invoiceId === invoice.id),
  }));
  // Paged, generation-checked catalog. JSON content is in R2; binary files
  // remain lazy references. No device downloads an account's entire 5 GB.
  onStage('download');
  const context = { userId: user.id, token: await sessionToken(), signal,queued:false,onQueueStage:onStage };
  context.queued=await syncQueueEnabled(context);
  const catalog = await readR2Catalog(context);
  let cloudRows = catalog.rows as CloudDocument<unknown>[];
  const finalCloudRows = new Map<string, CloudWrite>(cloudRows.map((row) => [row.id, row]));
  const missingCloudKeys = new Set<string>();
  const repairedRowIds = new Set<string>();
  let availableFiles = new Map<string, string>();
  if (hasCloudFileReferences(cloudRows)) {
    const originals = cloudRows;
    const transfer = cloudFileTransfer({ userId: user.id, token: await sessionToken(), signal,queued:context.queued,
      availability: catalog.availability,
      localFiles: localCloudFileSources([localQuotes, invoiceBundles, localSettings], user.id, signal) });
    cloudRows = await transfer.download(cloudRows) as CloudDocument<unknown>[];
    availableFiles = transfer.available;
    transfer.missing.forEach((_reference, key) => missingCloudKeys.add(key));
    // A restored immutable generation can have a new key. Re-publish the
    // winning merged record, not an old cloud snapshot, to repair its links.
    const changedKeys = new Set([...transfer.recovered].filter(([oldKey, next]) => oldKey !== next.key).map(([oldKey]) => oldKey));
    for (const row of originals) if (referencesCloudKeys(row.payload, changedKeys)) repairedRowIds.add(row.id);
  }
  const acknowledged = new Set<string>();
  for (const record of [...localQuotes,...invoiceBundles]) {
    const remote=cloudRows.find((row)=>row.id===record.id);
    if (record.syncState==='pending' && remote && record.version===remote.version && record.updatedAt===remote.updated_at
      && await cloudContentEquals(record,remote.payload)) acknowledged.add(record.id);
  }
  const quotesResult = mergeCollection(user, 'quote', resolveMissingCloudFiles(localQuotes, availableFiles), cloudRows, acknowledged);
  const invoicesResult = mergeCollection(user, 'invoice', resolveMissingCloudFiles(invoiceBundles, availableFiles), cloudRows, acknowledged);
  const settingsResult = mergeAccountSettings(user, resolveMissingCloudFiles(localSettings, availableFiles), cloudRows);
  const repairTime = new Date().toISOString();
  for (const result of [quotesResult, invoicesResult]) {
    for (const record of result.merged) {
      if (!repairedRowIds.has(record.id) || result.rowsToUpsert.some((row) => row.id === record.id)) continue;
      const remote = cloudRows.find((row) => row.id === record.id)!;
      record.version = Math.max(record.version, remote.version) + 1;
      record.updatedAt = repairTime;
      result.rowsToUpsert.push({ id: record.id, user_id: user.id, document_type: remote.document_type,
        payload: { ...record, syncState: 'synced' }, version: record.version, updated_at: repairTime, deleted_at: record.deletedAt });
    }
  }
  if (repairedRowIds.has(user.id) && !settingsResult.rowToUpsert) {
    settingsResult.merged = { ...settingsResult.merged, cloudSettingsUpdatedAt: repairTime };
    settingsResult.rowToUpsert = { id: user.id, user_id: user.id, document_type: 'quote',
      payload: { recordType: 'settings', settings: cloudSettingsFrom(settingsResult.merged) },
      version: Number(cloudRows.find((row) => row.id === user.id)?.version ?? 0) + 1, updated_at: repairTime, deleted_at: null };
  }
  const rowsToUpsert = [
    ...quotesResult.rowsToUpsert,
    ...invoicesResult.rowsToUpsert,
    ...(settingsResult.rowToUpsert ? [settingsResult.rowToUpsert] : []),
  ];
  // Verified copy-on-write migration: unchanged legacy rows also move to R2.
  // A failed/partial migration leaves the old payload or a verified R2 pointer.
  for (const row of cloudRows) if (catalog.legacyIds.has(row.id) && !rowsToUpsert.some((next) => next.id === row.id)) rowsToUpsert.push(row);
  const revisions = new Map(cloudRows.map((row) => [row.id, row.cloud_revision ?? 0]));
  if (rowsToUpsert.length > 0) {
    signal.throwIfAborted();
    onStage('upload');
    for (const rowToUpsert of rowsToUpsert) {
    const { error: upsertError } = await withDatabaseRetry(async (requestSignal) => {
      const accessToken = await sessionToken();
      requestSignal.throwIfAborted();
      for (let attempt = 0; attempt < 2; attempt++) {
        onStage('upload');
        const transfer = cloudFileTransfer({ userId: user.id, token: accessToken, signal: requestSignal,queued:context.queued,onUploadWaiting:()=>onStage('upload-queued'),onUploadStart:()=>onStage('upload') });
        const row = await transfer.upload({ ...rowToUpsert, cloud_revision: revisions.get(rowToUpsert.id) ?? -1 }) as CloudWrite;
        try {
          const result = await writeR2Row(row, { ...context, token: accessToken, signal: requestSignal });
          revisions.set(row.id, result.revision);
          for(const collection of [quotesResult.merged,invoicesResult.merged]){
            const record=collection.find(item=>item.id===row.id);
            if(record&&result.trashExpiresAt){record.trashExpiresAt=result.trashExpiresAt;record.deletedAt=result.deletedAt||record.deletedAt;}
          }
          finalCloudRows.set(row.id, row);
          return { error: null };
        } catch (error) {
        if (attempt === 0 && error instanceof Error && error.message === 'cloud_file_unavailable') {
          clearCloudFileCache(user.id);
          continue; // Re-upload from intact local bytes, with a fresh key.
        }
        throw error;
        }
      }
      return { error: { message: 'Cloud upload failed' } };
    }, { signal,...(context.queued?{maxAttempts:1,timeoutMs:15*60_000}:{}) });
    if (upsertError) throw upsertError;
    }
  }
  for (const result of [quotesResult,invoicesResult]) for (const record of result.merged) record.cloudRevision = revisions.get(record.id);
  settingsResult.merged.cloudCatalogRevision = revisions.get(user.id);
  // The user can keep editing while the network is busy. Re-read local data
  // so an older upload snapshot cannot overwrite edits made during the sync.
  assertLocalDataOwner(user.id);
  const [latestQuotes, latestInvoices, latestPayments, latestSettings] = await Promise.all([listQuotes(true), listInvoices(true), listPayments(), getSettings()]);
  const initialById=new Map<string,SyncRecord>([...localQuotes,...localInvoices].map(record=>[record.id,record]));
  for(const latest of [...latestQuotes,...latestInvoices]){
    const initial=initialById.get(latest.id); const remote=cloudRows.find(row=>row.id===latest.id);
    if(latest.syncState!=='pending'||!remote||remote.purged_at||!initial||acknowledged.has(latest.id))continue;
    const editedDuringSync=latest.version>initial.version||latest.updatedAt>initial.updatedAt;
    const remoteChanged=initial.cloudRevision!==undefined ? initial.cloudRevision!==remote.cloud_revision
      : remote.version>initial.version||remote.updated_at>initial.updatedAt;
    if(editedDuringSync&&remoteChanged)throw new Error('cloud_document_conflict');
  }
  const remoteSettingsRow=cloudRows.find(row=>row.id===user.id);
  if((latestSettings.cloudSettingsUpdatedAt??'')>(localSettings.cloudSettingsUpdatedAt??'')&&remoteSettingsRow
    &&localSettings.cloudCatalogRevision!==undefined&&localSettings.cloudCatalogRevision!==remoteSettingsRow.cloud_revision
    &&remoteSettingsRow.updated_at>(localSettings.cloudSettingsUpdatedAt??''))throw new Error('cloud_document_conflict');
  const mergedQuotes = resolveMissingCloudFiles(preserveNewerLocal(quotesResult.merged, latestQuotes), availableFiles);
  const latestBundles = latestInvoices.map((invoice) => ({ ...invoice, cloudPayments: latestPayments.filter((payment) => payment.invoiceId === invoice.id) }));
  const mergedBundles = resolveMissingCloudFiles(preserveNewerLocal(invoicesResult.merged, latestBundles), availableFiles);
  const mergedSettings = resolveMissingCloudFiles((latestSettings.cloudSettingsUpdatedAt ?? '') > (settingsResult.merged.cloudSettingsUpdatedAt ?? '')
    ? latestSettings : { ...latestSettings, ...cloudSettingsFrom(settingsResult.merged) }, availableFiles);
  mergedSettings.cloudCatalogRevision = revisions.get(user.id);
  const mergedInvoices: Invoice[] = mergedBundles.map(({ cloudPayments: _cloudPayments, ...invoice }) => invoice);
  const mergedPayments = mergedBundles.flatMap((invoice) => invoice.cloudPayments ?? []);
  const missingKeys = missingCloudFileKeys([mergedQuotes.filter((record) => !record.deletedAt), mergedBundles.filter((record) => !record.deletedAt), mergedSettings.company]);
  const stillReferenced = cloudReferenceKeys([...finalCloudRows.values()].filter((row) => !row.deleted_at));
  missingCloudKeys.forEach((key) => { if (stillReferenced.has(key)) missingKeys.add(key); });
  mergedSettings.cloudMissingFileKeys = [...missingKeys];
  signal.throwIfAborted();
  onStage('save');
  let localCacheSaved = true;
  try { await saveSyncSnapshot(mergedQuotes, mergedInvoices, mergedPayments, mergedSettings, user.id); }
  catch (error) {
    if (!(error instanceof DOMException) || error.name !== 'QuotaExceededError') throw error;
    // Cloud data remains accessible in this session without deleting Local
    // originals or unsynced edits to make room. The UI must disclose this.
    localCacheSaved = false;
  }
  // The hourly server job owns automatic retirement. Starting destructive
  // maintenance from a disposable browser request can strand its durable guard
  // when the customer closes the tab immediately after a successful sync.
  return {
    trash:[...mergedQuotes.filter(record=>record.deletedAt&&!record.purgedAt).map(record=>({...record,kind:'quote' as const})),
      ...mergedInvoices.filter(record=>record.deletedAt&&!record.purgedAt).map(record=>({...record,kind:'invoice' as const}))],
    quotes: mergedQuotes.filter((quote) => !quote.deletedAt).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    invoices: mergedInvoices.filter((invoice) => !invoice.deletedAt).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    payments: mergedPayments.sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    settings: mergedSettings,
    missingFileCount: missingKeys.size,
    localCacheSaved,
    uploadedCount:rowsToUpsert.length,
    pendingLocalCount:[...mergedQuotes,...mergedInvoices].filter(row=>row.syncState==='pending').length+
      ((latestSettings.cloudSettingsUpdatedAt??'')>(settingsResult.merged.cloudSettingsUpdatedAt??'')?1:0),
  };
}

// Login, a manual click and an autosave can request sync simultaneously. Share
// one operation per account rather than uploading multiple copies in parallel.
type SyncResult = Awaited<ReturnType<typeof performSync>>;
const activeSyncs = new Map<string, { promise: Promise<SyncResult>; controller:AbortController; stage: SyncStage; listeners: Set<(stage: SyncStage) => void> }>();

export function cancelDocumentSyncs(){
  for(const entry of activeSyncs.values())entry.controller.abort(new Error('Session changed'));
  activeSyncs.clear();
}

export function syncAllDocuments(user: User, onStage?: (stage: SyncStage) => void): Promise<SyncResult> {
  const running = activeSyncs.get(user.id);
  if (running) {
    if (onStage) { running.listeners.add(onStage); onStage(running.stage); }
    return running.promise;
  }
  const entry = { promise: null as unknown as Promise<SyncResult>, controller:new AbortController(),stage: 'local' as SyncStage, listeners: new Set(onStage ? [onStage] : []) };
  activeSyncs.set(user.id, entry);
  entry.promise = withAbortTimeout((signal) => performSync(user, signal, (stage) => {
    entry.stage = stage;
    entry.listeners.forEach((listener) => listener(stage));
  }), 15 * 60_000,entry.controller.signal)
    .catch((reason) => { throw new CloudSyncError(entry.stage, reason); })
    .finally(() => { if(activeSyncs.get(user.id)===entry)activeSyncs.delete(user.id); });
  return entry.promise;
}

export async function syncQuotes(user: User) {
  return (await syncAllDocuments(user)).quotes;
}
